import { useMemo } from 'react';

export type DashboardRange = '7d' | '14d' | '30d' | 'all' | 'latest';

const rangeDays: Record<Exclude<DashboardRange, 'all' | 'latest'>, number> = {
  '7d': 7,
  '14d': 14,
  '30d': 30,
};

const formatMetricsDay = (value: Date): string => value.toISOString().slice(0, 10);

export const getDashboardRangeDays = (range: DashboardRange): number | null => {
  if (range === 'all' || range === 'latest') return null;
  return rangeDays[range];
};

export const formatRangeWindowNoDataText = (
  baseText: string,
  range: DashboardRange,
  hasCustomDates: boolean,
): string => {
  if (hasCustomDates) return baseText;
  const rangeDaysCount = getDashboardRangeDays(range);
  if (rangeDaysCount === null) return baseText;
  return `${baseText} for the past ${rangeDaysCount} days`;
};

type DashboardDateRange = {
  rangeStartIso: string | null;
  rangeEndIso: string | null;
  rangeStartDay: string | null;
  rangeEndDay: string | null;
  previousRangeStartIso: string | null;
  previousRangeEndIso: string | null;
  previousRangeStartDay: string | null;
  previousRangeEndDay: string | null;
};

export const useDashboardDateRange = (range: DashboardRange): DashboardDateRange => {
  const rangeDates = useMemo(() => {
    const days = getDashboardRangeDays(range);
    if (!days) return null;
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(end.getDate() - days);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }, [range]);
  const previousRangeDates = useMemo(() => {
    if (!rangeDates) return null;
    const days = getDashboardRangeDays(range);
    if (!days) return null;
    const end = new Date(rangeDates.start);
    const start = new Date(end);
    start.setDate(end.getDate() - days);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }, [range, rangeDates]);
  const rangeStartIso = useMemo(
    () => (rangeDates ? rangeDates.start.toISOString() : null),
    [rangeDates],
  );
  const rangeEndIso = useMemo(
    () => (rangeDates ? rangeDates.end.toISOString() : null),
    [rangeDates],
  );
  const previousRangeStartIso = useMemo(
    () => (previousRangeDates ? previousRangeDates.start.toISOString() : null),
    [previousRangeDates],
  );
  const previousRangeEndIso = useMemo(
    () => (previousRangeDates ? previousRangeDates.end.toISOString() : null),
    [previousRangeDates],
  );
  const previousRangeStartDay = useMemo(
    () => (previousRangeDates ? formatMetricsDay(previousRangeDates.start) : null),
    [previousRangeDates],
  );
  const previousRangeEndDay = useMemo(
    () => (previousRangeDates ? formatMetricsDay(previousRangeDates.end) : null),
    [previousRangeDates],
  );
  const rangeStartDay = useMemo(
    () => (rangeDates ? formatMetricsDay(rangeDates.start) : null),
    [rangeDates],
  );
  const rangeEndDay = useMemo(
    () => (rangeDates ? formatMetricsDay(rangeDates.end) : null),
    [rangeDates],
  );

  return {
    rangeStartIso,
    rangeEndIso,
    rangeStartDay,
    rangeEndDay,
    previousRangeStartIso,
    previousRangeEndIso,
    previousRangeStartDay,
    previousRangeEndDay,
  };
};
